// "I'm running late" — how many minutes behind schedule to quote, replacing
// the hardcoded "about 15 minutes".
//
// Two situations, and they want different answers:
//
//   • The patient is ALREADY WAITING (their slot has passed). The number they
//     can check against their own watch is `now − their scheduled start`.
//     Simple, needs no other data, and impossible to argue with.
//
//   • The patient is NOT DUE YET and is being warned in advance. Their own slot
//     says nothing, so fall back to how the provider is actually running:
//     `queue_entries.serving_at` vs each appointment's scheduled `start_time`.
//
// `computeProviderDelayMinutes` tries them in that order.
//
// Self-contained (no workspace imports) so it runs under the node:test harness.

/** Fallback when the delay cannot be determined. */
export const DEFAULT_LATE_MINUTES = 15;

/** Rounding granularity — nobody says "we're 23 minutes behind". */
export const LATE_ROUNDING_MINUTES = 5;

/** Beyond this, the number reads as absurd and a hand-written note is better. */
export const MAX_LATE_MINUTES = 180;

/** One of today's queue entries for the provider, joined to its appointment. */
export interface ProviderQueueEntry {
  /** Scheduled start of the linked appointment, HH:MM[:SS]. Null for walk-ins. */
  appointmentStartTime?: string | null;
  /** When the patient was actually taken in (ISO). */
  servingAt?: string | null;
  /** Fallback signal when serving_at was never stamped. */
  calledAt?: string | null;
  status?: string | null;
}

/** A scheduled appointment for the provider today. */
export interface ProviderScheduledAppointment {
  startTime?: string | null;
  /** True when the patient has been seen (or is being seen) already. */
  started?: boolean;
}

export interface ProviderDelayInput {
  /** Clinic-local date of the queue/appointments, YYYY-MM-DD. */
  date: string | null | undefined;
  /**
   * Scheduled start (HH:MM) of the appointment belonging to the person being
   * messaged. When it has already passed, this is the STRONGEST signal: it is
   * literally how long that patient has been kept waiting, and it needs no
   * queue data at all.
   */
  recipientStartTime?: string | null;
  queue: ProviderQueueEntry[];
  appointments: ProviderScheduledAppointment[];
  now: Date;
}

function timeToMinutes(value: string | null | undefined): number | null {
  const m = /^(\d{1,2}):(\d{2})(?::\d{2})?$/.exec((value ?? '').trim());
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (h > 23 || min > 59) return null;
  return h * 60 + min;
}

/** Minutes past clinic-local midnight for an ISO instant, in local time. */
function instantToMinutesOfDay(iso: string | null | undefined, date: string): number | null {
  if (!iso) return null;
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return null;
  const dayMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  if (!dayMatch) return null;
  const midnight = new Date(
    Number(dayMatch[1]),
    Number(dayMatch[2]) - 1,
    Number(dayMatch[3]),
    0,
    0,
    0,
    0,
  );
  return Math.round((at.getTime() - midnight.getTime()) / 60_000);
}

function roundUp(minutes: number): number {
  return Math.ceil(minutes / LATE_ROUNDING_MINUTES) * LATE_ROUNDING_MINUTES;
}

/**
 * How many minutes behind schedule we are, from the recipient's point of view.
 *
 * Three signals, strongest first:
 *
 *  1. WAITING — the recipient's own appointment time has passed:
 *     `now − their scheduled start`. Exactly how long THEY have been kept
 *     waiting, needs no queue data, and is the number they can verify against
 *     their own watch. Preferred whenever it applies.
 *  2. OBSERVED — the recipient is not due yet, so fall back to how the provider
 *     is actually running: `serving_at − scheduled start` for the most recently
 *     started appointment. Lets a future patient be warned in advance.
 *  3. OVERDUE — nothing started yet, but a slot that should have begun has not:
 *     `now − its scheduled start`. Covers a session that simply began late.
 *
 * Returns null when on time, when there is no usable data, or when the delay
 * exceeds MAX_LATE_MINUTES — callers fall back to DEFAULT_LATE_MINUTES.
 */
export function computeProviderDelayMinutes(input: ProviderDelayInput): number | null {
  const date = (input.date ?? '').trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return null;

  const nowMinutesEarly = instantToMinutesOfDay(input.now.toISOString(), date);

  // ── 1. The recipient is already waiting — the simplest, truest number ──
  const recipientStart = timeToMinutes(input.recipientStartTime);
  if (recipientStart !== null && nowMinutesEarly !== null) {
    const waited = nowMinutesEarly - recipientStart;
    if (waited > 0) {
      const rounded = roundUp(waited);
      return rounded > MAX_LATE_MINUTES ? null : rounded;
    }
  }

  // ── 2. Observed lateness from actual start times ──
  let observed: { scheduled: number; delay: number } | null = null;
  for (const entry of input.queue) {
    const scheduled = timeToMinutes(entry.appointmentStartTime);
    if (scheduled === null) continue; // walk-in — no schedule to be late against
    const actual =
      instantToMinutesOfDay(entry.servingAt, date) ?? instantToMinutesOfDay(entry.calledAt, date);
    if (actual === null) continue;
    const delay = actual - scheduled;
    // Keep the LATEST-scheduled started appointment: it is the freshest read on
    // how the session is running now.
    if (!observed || scheduled > observed.scheduled) {
      observed = { scheduled, delay };
    }
  }
  if (observed) {
    if (observed.delay <= 0) return null; // running on time (or early)
    const rounded = roundUp(observed.delay);
    return rounded > MAX_LATE_MINUTES ? null : rounded;
  }

  // ── 3. Nothing started — is something already overdue? ──
  const nowMinutes = nowMinutesEarly;
  if (nowMinutes === null) return null;

  let earliestOverdue: number | null = null;
  for (const appt of input.appointments) {
    if (appt.started) continue;
    const scheduled = timeToMinutes(appt.startTime);
    if (scheduled === null || scheduled >= nowMinutes) continue;
    if (earliestOverdue === null || scheduled < earliestOverdue) earliestOverdue = scheduled;
  }
  if (earliestOverdue === null) return null;

  const delay = nowMinutes - earliestOverdue;
  if (delay <= 0) return null;
  const rounded = roundUp(delay);
  return rounded > MAX_LATE_MINUTES ? null : rounded;
}

/** Minutes to use in the message — the measured delay, or the default. */
export function resolveLateMinutes(input: ProviderDelayInput): number {
  return computeProviderDelayMinutes(input) ?? DEFAULT_LATE_MINUTES;
}

/** Preset label, e.g. "I'm running late (20 min)". */
export function runningLateLabel(minutes: number): string {
  return `I'm running late (${minutes} min)`;
}

/** Message body for the running-late preset. */
export function runningLateMessage(patientName: string, minutes: number): string {
  const who = patientName?.trim() || 'there';
  return `Hi ${who} — quick heads up that we're running about ${minutes} minutes behind schedule today. Sorry for the inconvenience, see you soon!`;
}
